import type { UiState } from 'instantsearch.js';
import { debounce } from 'instantsearch.js/es/lib/utils/debounce';
import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import type { InstantSearchNextRouting } from 'react-instantsearch-nextjs';

import type { IAlgoliaRouteState, IUiState, SFCCFilterMap } from '@libs/algolia';
import { AlgoliaIndice, createAlgoliaClient } from '@libs/algolia';
import { initAlgoliaTracking } from '@libs/analytics';
import { IS_SSR, useDecisions } from '@libs/common-next';

import type { Facets } from '@/types/facetedNavigation';
import { filterOutSeoFriendlyFacets, mapFacetValuesToUrl } from '@/utils/facetedNavigationClientUtils';

import {
    addUrlRefinementsToUiState,
    disableAnalytics,
    getInitialSearchUserTokenAfterConsent,
    getInitialUrlRefinements,
    getValidUserTokenFromRequests,
    prepareInitialUrlRefinementRequests,
    stripAnalyticsWhenUnattributable,
    type UrlRefinement,
} from './algoliaRequestUtils';
import { getQueryParamsFromState, getRefinementsFromQueryParams, getRouteFromState, getStateFromRoute } from './utils';

type AlgoliaClient = ReturnType<typeof createAlgoliaClient>;
type AlgoliaSearchRequests = Parameters<AlgoliaClient['search']>[0];
type AlgoliaSearchPromise = ReturnType<AlgoliaClient['search']>;

type InstantSearchStateChange = {
    uiState: UiState;
    setUiState: (uiState: UiState | ((previousUiState: UiState) => UiState)) => void;
};

type SearchSession = {
    refinementKey: string;
    refinements: UrlRefinement[];
    lastAppliedUiState: string | null;
    initialRequest: {
        hasAttribution: boolean;
        promise: AlgoliaSearchPromise | null;
        requests: AlgoliaSearchRequests | null;
        userToken?: string;
    };
    userInteracted: boolean;
};

type UseSearchLayoutProps = {
    isSearchPage: boolean;
    urlFilters: SFCCFilterMap[] | Facets[];
};

const createSearchSession = (refinementKey: string, refinements: UrlRefinement[]): SearchSession => ({
    refinementKey,
    refinements,
    lastAppliedUiState: null,
    initialRequest: {
        hasAttribution: false,
        promise: null,
        requests: null,
        userToken: undefined,
    },
    userInteracted: false,
});

/**
 * Creates an InstantSearch-compatible Algolia client bound to the current search session.
 *
 * The returned client stays stable for React memoization while checking `sessionRef` whenever a
 * search starts, so route-driven refinements can reset without recreating InstantSearch. During SSR
 * it strips analytics from search requests. In the browser, the first protected search before user
 * interaction may wait briefly for consent and a valid analytics user token, then re-applies any
 * initial URL refinements and attributes analytics at most once. While that protected request is
 * pending, later pre-interaction searches reuse the same promise and update the queued request.
 * The queued initial search is still sent if the user interacts before attribution readiness
 * resolves; stale response ordering is left to InstantSearch's query lifecycle instead of returning
 * a fabricated empty result. If the protected request fails or completes without attribution, the
 * cached promise is cleared so a later pre-interaction search can retry; after user interaction,
 * normal searches only keep analytics metadata when the request is attributable.
 *
 * @param sessionRef - Mutable search session used to preserve initial URL refinements and
 * analytics attribution state across InstantSearch requests.
 * @returns Algolia client wrapper used by InstantSearch.
 */
const createSearchClient = (sessionRef: RefObject<SearchSession>): AlgoliaClient => {
    const client = createAlgoliaClient({ caller: 'web:plp:instantSearch-client' });

    if (IS_SSR) {
        return {
            ...client,
            search: (requests: Parameters<AlgoliaClient['search']>[0]) => client.search(disableAnalytics(requests)),
        } as AlgoliaClient;
    }

    return {
        ...client,
        search: (requests: Parameters<AlgoliaClient['search']>[0]) => {
            const session = sessionRef.current;
            const shouldProtectInitialSearch = !session.userInteracted;

            if (shouldProtectInitialSearch) {
                session.initialRequest.requests = requests;
                session.initialRequest.userToken =
                    getValidUserTokenFromRequests(requests) ?? session.initialRequest.userToken;

                if (!session.initialRequest.promise) {
                    session.initialRequest.promise = getInitialSearchUserTokenAfterConsent(
                        () => session.initialRequest.userToken
                    )
                        .then(userToken => {
                            const searchRequests = session.initialRequest.requests ?? requests;
                            const { preparedRequests, didEnableAnalyticsAttribution } =
                                prepareInitialUrlRefinementRequests({
                                    searchRequests,
                                    initialUrlRefinements: session.refinements,
                                    userToken,
                                    hasAlreadyAttributedInitialRequest: session.initialRequest.hasAttribution,
                                });

                            return Promise.resolve(client.search(preparedRequests)).then(searchResponse => {
                                if (didEnableAnalyticsAttribution) {
                                    session.initialRequest.hasAttribution = true;
                                }

                                if (!session.initialRequest.hasAttribution) {
                                    session.initialRequest.promise = null;
                                }

                                return searchResponse;
                            });
                        })
                        .catch(error => {
                            session.initialRequest.promise = null;
                            throw error;
                        });
                }

                return session.initialRequest.promise;
            }

            return client.search(stripAnalyticsWhenUnattributable(requests));
        },
        searchForFacetValues: (requests: Parameters<AlgoliaClient['searchForFacetValues']>[0]) =>
            client.searchForFacetValues(stripAnalyticsWhenUnattributable(requests)),
    } as AlgoliaClient;
};

export const useInstantSearchSessionManager = ({ isSearchPage, urlFilters }: UseSearchLayoutProps) => {
    const { browse_drr } = useDecisions();
    const isBrowseDrrEnabled = !isSearchPage && browse_drr;
    const initialUrlRefinements = useMemo(
        () => getInitialUrlRefinements(urlFilters, isSearchPage),
        [isSearchPage, urlFilters]
    );
    const initialUrlRefinementsKey = useMemo(() => JSON.stringify(initialUrlRefinements), [initialUrlRefinements]);
    const sessionRef = useRef(createSearchSession(initialUrlRefinementsKey, initialUrlRefinements));

    useEffect(() => {
        if (sessionRef.current.refinementKey === initialUrlRefinementsKey) {
            return;
        }

        sessionRef.current = createSearchSession(initialUrlRefinementsKey, initialUrlRefinements);
    }, [initialUrlRefinements, initialUrlRefinementsKey]);

    const markUserInteractedWithSearch = useCallback(() => {
        sessionRef.current.userInteracted = true;
    }, []);

    const onStateChange = useCallback((updatedSearchState: InstantSearchStateChange) => {
        const session = sessionRef.current;
        const uiStateToApply = session.userInteracted
            ? updatedSearchState.uiState
            : addUrlRefinementsToUiState(updatedSearchState.uiState, session.refinements);

        // With `onStateChange` provided, InstantSearch is "controlled": every
        // `helper.search()` (refinement clicks, pagination, search box, sort, etc.)
        // is routed here and only actually searches when we call `setUiState`.
        //
        // We must NOT skip based on "first emission". The previous code skipped
        // very first emission to swallow a duplicate state change that older
        // react-instantsearch versions fired while hydrating the SSR results.
        // react-instantsearch-nextjs 1.2.x hydrates from `initialResults` without
        // re-emitting that state change, so skipping the first emission instead
        // swallowed the user's first refinement — which is why filtering required
        // multiple clicks.
        //
        // This handler is wrapped in a trailing debounce below, so rapid emissions
        // may collapse to the latest state before this code runs. Once it runs,
        // skip only genuinely redundant applied states (a no-op state change,
        // e.g. from insights/userToken churn) while still applying non-redundant
        // debounced state changes regardless of first/second emission order.
        const nextUiState = JSON.stringify(uiStateToApply);
        if (nextUiState === session.lastAppliedUiState) {
            return;
        }

        session.lastAppliedUiState = nextUiState;
        updatedSearchState.setUiState(uiStateToApply);
    }, []);

    const debouncedOnStateChange = useMemo(() => debounce(onStateChange, 200), [onStateChange]);

    const routing: InstantSearchNextRouting<UiState, UiState> = useMemo(
        () => ({
            router: {
                // overriding default behavior so that the whole page does not re-render when adding/removing brands
                push(url: string) {
                    if (globalThis.history) {
                        /**
                         * Since Next version 14, window.history.pushState is monkey patched to work with Next Router
                         * Setting { _N: true } data, triggers the original pushState method, without triggering Next's monkey patched version
                         *
                         * This is a temporary solution
                         *
                         * @see https://github.com/vercel/next.js/blob/5969a097b44eae1dd314e8f84d13733edf73af3e/packages/next/src/client/components/app-router.tsx#L481
                         */
                        globalThis.history.pushState({ _N: true }, '', url);
                    }
                },
                createURL({ qsModule, routeState, location }) {
                    const pathnameToUse = mapFacetValuesToUrl(routeState, location);

                    const queryParameters = getQueryParamsFromState(
                        filterOutSeoFriendlyFacets(routeState),
                        isSearchPage,
                        isBrowseDrrEnabled
                    );

                    const queryString = qsModule.stringify(queryParameters, {
                        addQueryPrefix: true,
                        arrayFormat: 'repeat',
                    });

                    return `${location.protocol}//${location.host}${pathnameToUse}${queryString}`;
                },
                parseURL({ qsModule, location }) {
                    const parsedqs = qsModule.parse(location.search.slice(1));
                    return getRefinementsFromQueryParams(parsedqs, urlFilters);
                },
            },

            stateMapping: {
                stateToRoute(uiState: unknown) {
                    const indexUiState = (uiState as IUiState)[AlgoliaIndice.PRODUCTS];
                    return getRouteFromState(indexUiState);
                },
                routeToState(routeState: unknown) {
                    const uiState = {
                        [AlgoliaIndice.PRODUCTS]: getStateFromRoute(
                            routeState as IAlgoliaRouteState,
                            isSearchPage,
                            isBrowseDrrEnabled
                        ),
                    };

                    return sessionRef.current.userInteracted
                        ? uiState
                        : addUrlRefinementsToUiState(uiState, initialUrlRefinements);
                },
            },
        }),
        [isBrowseDrrEnabled, initialUrlRefinements, isSearchPage, urlFilters]
    );

    // Kick off insights init in the background for click/conversion tracking.
    // Initial searches use `getInitialSearchUserTokenAfterConsent` inside the
    // search client so they can wait briefly for OneTrust and `_ga` before the
    // protected query is sent. `search-insights` buffers events fired before
    // init completes, and `initAlgoliaTracking` self-heals if OneTrust loads
    // late.
    useEffect(() => {
        void initAlgoliaTracking();
    }, []);

    // New searches read from sessionRef so the client can stay stable while URL
    // refinement state is reset after route changes.
    const searchClient = useMemo(() => createSearchClient(sessionRef), []);

    return {
        searchClient,
        routing,
        onStateChange: debouncedOnStateChange,
        markUserInteractedWithSearch,
    };
};
